import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  FileText,
  Loader2,
  Pause,
  PlayCircle,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReviewModal } from "@/components/flows/ReviewModal";
import { Markdown } from "@/components/shared/Markdown";
import { downloadAttachment } from "@/lib/downloadAttachment";
import { flowService } from "@/lib/services";
import type { FlowRun, FlowRunStep } from "@/types";

export function FlowRunDetail() {
  const { flowId, runId } = useParams<{ flowId: string; runId: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<FlowRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (flowId && runId) {
      loadRun();
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [flowId, runId]);

  const loadRun = async () => {
    if (!flowId || !runId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await flowService.getRun(flowId, runId);
      setRun(response.data);

      // If the run is still in progress, start polling for updates
      const status = response.data.status;
      if ((status === "running" || status === "pending") && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const pollResponse = await flowService.getRun(flowId, runId);
            setRun(pollResponse.data);

            const newStatus = pollResponse.data.status;
            if (newStatus !== "running" && newStatus !== "pending") {
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
            }
          } catch {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load run");
      console.error("Error loading run:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStepExpanded = (stepId: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-green-400" />;
      case "running":
        return <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-red-400" />;
      case "paused":
        return <Pause className="h-5 w-5 text-yellow-400" />;
      case "cancelled":
        return <XCircle className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-400 bg-green-500/10 border-green-500/30";
      case "running":
        return "text-blue-400 bg-blue-500/10 border-blue-500/30";
      case "failed":
        return "text-red-400 bg-red-500/10 border-red-500/30";
      case "paused":
        return "text-yellow-400 bg-yellow-500/10 border-yellow-500/30";
      case "cancelled":
        return "text-gray-400 bg-gray-500/10 border-gray-500/30";
      default:
        return "text-gray-400 bg-gray-500/10 border-gray-500/30";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const calculateDuration = (start?: string, end?: string) => {
    if (!start || !end) return "N/A";
    const duration = new Date(end).getTime() - new Date(start).getTime();
    return `${Math.round(duration / 1000)}s`;
  };

  // Find the paused step (if any), falling back to the last completed step
  // when the run is paused but no individual step has status "paused" (data inconsistency)
  const pausedStep = run?.steps?.find((s) => s.status === "paused");
  const reviewStep =
    pausedStep ||
    (run?.status === "paused" ? [...(run?.steps || [])].reverse().find((s) => s.status === "completed") : undefined);

  const handleApproveReview = useCallback(
    async (modifiedOutput?: string) => {
      if (!flowId || !runId) return;

      try {
        setIsResuming(true);
        setShowReviewModal(false);

        // Resume the flow with approval
        await flowService.resumeRun(flowId, runId, true, modifiedOutput);

        // Poll for updated run status
        pollRef.current = setInterval(async () => {
          try {
            const runResponse = await flowService.getRun(flowId, runId);
            setRun(runResponse.data);

            const newStatus = runResponse.data.status;
            if (newStatus === "completed" || newStatus === "failed" || newStatus === "cancelled") {
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
              setIsResuming(false);
            } else if (newStatus === "paused") {
              // Paused again on a subsequent step — stop polling and let user review
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
              setIsResuming(false);
            }
          } catch (err) {
            console.error("Error polling run status:", err);
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setIsResuming(false);
            setError("Failed to check run status after resume");
          }
        }, 2000);
      } catch (err) {
        console.error("Error resuming flow:", err);
        setError(err instanceof Error ? err.message : "Failed to resume flow");
        setIsResuming(false);
      }
    },
    [flowId, runId],
  );

  const handleRejectReview = useCallback(async () => {
    if (!flowId || !runId) return;

    try {
      setIsResuming(true);
      await flowService.cancelRun(flowId, runId, "Rejected by user during review");
      setShowReviewModal(false);
      // Reload the run to show cancelled state
      await loadRun();
    } catch (err) {
      console.error("Error cancelling flow:", err);
      setError(err instanceof Error ? err.message : "Failed to cancel flow");
    } finally {
      setIsResuming(false);
    }
  }, [flowId, runId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/flows/${flowId}`)}
            className="p-2 hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-content-primary">Flow Run Not Found</h1>
          </div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <h3 className="font-medium text-red-400">Error</h3>
          </div>
          <p className="text-red-300">{error || "Run not found"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/flows/${flowId}`)}
            className="p-2 hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-content-primary">Flow Run Details</h1>
            <p className="text-sm text-content-tertiary">Run ID: {run.uuid.slice(0, 8)}...</p>
          </div>
        </div>
        <div className={`px-4 py-2 rounded-lg border ${getStatusColor(run.status)}`}>
          <div className="flex items-center gap-2">
            {getStatusIcon(run.status)}
            <span className="font-medium capitalize">{run.status}</span>
          </div>
        </div>
      </div>

      {/* Run Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-sm mb-1">
            <Calendar className="h-4 w-4" />
            Started At
          </div>
          <div className="text-content-primary font-medium">{formatDate(run.startedAt)}</div>
        </div>

        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-sm mb-1">
            <CheckCircle2 className="h-4 w-4" />
            Completed At
          </div>
          <div className="text-content-primary font-medium">{formatDate(run.completedAt)}</div>
        </div>

        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-sm mb-1">
            <Clock className="h-4 w-4" />
            Duration
          </div>
          <div className="text-content-primary font-medium">{calculateDuration(run.startedAt, run.completedAt)}</div>
        </div>

        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <div className="flex items-center gap-2 text-content-tertiary text-sm mb-1">
            <Zap className="h-4 w-4" />
            Total Tokens
          </div>
          <div className="text-content-primary font-medium">{run.totalTokens.toLocaleString()}</div>
        </div>
      </div>

      {/* Input */}
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-content-primary mb-4">Input</h2>
        <div className="p-4 bg-surface-secondary/50 rounded-lg border border-border-secondary">
          <Markdown content={run.inputPrompt} />
        </div>
      </div>

      {/* Steps */}
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-content-primary mb-4">Steps</h2>
        <div className="space-y-3">
          {run.steps && run.steps.length > 0 ? (
            run.steps.map((step: FlowRunStep) => (
              <div
                key={step.id}
                className={`border rounded-lg transition-colors ${
                  expandedSteps.has(step.id)
                    ? "bg-surface-secondary/50 border-border-secondary"
                    : "bg-surface-secondary/30 border-border-secondary/50"
                }`}
              >
                <button
                  onClick={() => toggleStepExpanded(step.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-interactive-hover transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-surface-secondary text-sm font-medium text-content-tertiary">
                      {step.stepNumber + 1}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-content-primary">{step.label || step.agentName || "Step"}</p>
                      {step.tokensUsed > 0 && (
                        <p className="text-xs text-content-tertiary">{step.tokensUsed.toLocaleString()} tokens used</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 rounded-full text-xs ${getStatusColor(step.status)}`}>{step.status}</div>
                    {expandedSteps.has(step.id) ? (
                      <ChevronUp className="h-5 w-5 text-content-tertiary" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-content-tertiary" />
                    )}
                  </div>
                </button>

                {expandedSteps.has(step.id) && (
                  <div className="px-4 pb-4 space-y-4">
                    {step.inputContent && (
                      <div>
                        <span className="block text-sm font-medium text-content-secondary mb-2">Input</span>
                        <div className="p-3 bg-surface-primary rounded-lg border border-border-secondary">
                          <Markdown content={step.inputContent} />
                        </div>
                      </div>
                    )}

                    {step.outputContent && (
                      <div>
                        <span className="block text-sm font-medium text-content-secondary mb-2">Output</span>
                        <div className="p-3 bg-surface-primary rounded-lg border border-border-secondary max-h-96 overflow-y-auto">
                          <Markdown content={step.outputContent} />
                        </div>
                      </div>
                    )}

                    {step.errorMessage && (
                      <div>
                        <span className="block text-sm font-medium text-red-400 mb-2">Error</span>
                        <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                          <p className="text-sm text-red-300 whitespace-pre-wrap">{step.errorMessage}</p>
                        </div>
                      </div>
                    )}

                    {step.attachments && step.attachments.length > 0 && (
                      <div>
                        <span className="block text-sm font-medium text-content-secondary mb-2">Attachments</span>
                        <div className="space-y-2">
                          {step.attachments.map((att) => (
                            <button
                              type="button"
                              key={att.uuid}
                              onClick={() => downloadAttachment({ attachmentUuid: att.uuid }, att.filename)}
                              className="flex items-center gap-3 p-3 w-full text-left bg-surface-primary rounded-lg border border-border-secondary hover:border-brand-primary transition-colors"
                            >
                              <FileText className="h-5 w-5 text-brand-primary shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-content-primary truncate">{att.filename}</div>
                                <div className="text-xs text-content-quaternary">
                                  {att.mimeType} &middot; {(att.size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                              <Download className="h-4 w-4 text-content-tertiary shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-xs text-content-quaternary">
                      {step.startedAt && <span>Started: {formatDate(step.startedAt)}</span>}
                      {step.completedAt && <span>Completed: {formatDate(step.completedAt)}</span>}
                      {step.startedAt && step.completedAt && (
                        <span>Duration: {calculateDuration(step.startedAt, step.completedAt)}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-content-tertiary text-center py-8">No steps found</p>
          )}
        </div>
      </div>

      {/* All Attachments */}
      {run.steps?.some((s) => s.attachments && s.attachments.length > 0) && (
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-content-primary mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand-primary" />
            Attachments
          </h2>
          <div className="space-y-2">
            {run.steps
              .filter((s) => s.attachments && s.attachments.length > 0)
              .flatMap((s) =>
                (s.attachments ?? []).map((att) => (
                  <button
                    type="button"
                    key={att.uuid}
                    onClick={() => downloadAttachment({ attachmentUuid: att.uuid }, att.filename)}
                    className="flex items-center gap-3 p-3 w-full text-left bg-surface-secondary rounded-lg border border-border-secondary hover:border-brand-primary transition-colors"
                  >
                    <FileText className="h-5 w-5 text-brand-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-content-primary truncate">{att.filename}</div>
                      <div className="text-xs text-content-quaternary">
                        {s.label || s.agentName || `Step ${s.stepNumber}`} &middot; {att.mimeType} &middot;{" "}
                        {(att.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                    <Download className="h-4 w-4 text-content-tertiary shrink-0" />
                  </button>
                )),
              )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {run.errorMessage && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <h3 className="font-medium text-red-400">Error</h3>
          </div>
          <p className="text-red-300 whitespace-pre-wrap">{run.errorMessage}</p>
        </div>
      )}

      {/* Paused Banner with Resume Action */}
      {run.status === "paused" && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Pause className="h-5 w-5 text-yellow-400" />
              <div>
                <h3 className="font-medium text-content-primary">Awaiting Review</h3>
                <p className="text-sm text-content-tertiary mt-1">
                  {reviewStep
                    ? `Step ${reviewStep.stepNumber + 1} (${reviewStep.agentName}) has completed and is waiting for your review before the flow continues.`
                    : "This flow is paused and waiting for your review."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button
                onClick={() => handleRejectReview()}
                disabled={isResuming}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResuming ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Cancel Run
              </button>
              {reviewStep && (
                <button
                  onClick={() => setShowReviewModal(true)}
                  disabled={isResuming}
                  className="flex items-center gap-2 px-5 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResuming ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Resuming...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-4 w-4" />
                      Review & Resume
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && run && reviewStep && (
        <ReviewModal
          flowRun={run}
          pausedStep={reviewStep}
          onApprove={handleApproveReview}
          onReject={handleRejectReview}
          onClose={() => setShowReviewModal(false)}
          isSubmitting={isResuming}
        />
      )}
    </div>
  );
}
